import { useState } from 'react'
import { Lock, Mail, LogIn, AlertTriangle, ShieldCheck, KeyRound, Fingerprint } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { authenticateWithPasskey, isWebAuthnSupported } from '../api/webauthn'

type Stage = 'credentials' | 'mfa'

export default function LoginPage() {
  const login = useAuthStore((s) => s.login)
  const checkAuth = useAuthStore((s) => s.checkAuth)
  const toast = useToastStore()
  const navigate = useNavigate()

  const [stage, setStage] = useState<Stage>('credentials')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [lockout, setLockout] = useState(false)
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  // ZER-334: ブラウザが Passkey 対応していなければボタン非表示
  const passkeySupported = isWebAuthnSupported()

  // 「あと○分後に再試行」等のロックアウトメッセージ検出
  const isLockoutMessage = (msg: string) =>
    /あと\d+分後に再試行/.test(msg) || /試行回数が上限/.test(msg)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const result = await login(email, password, stage === 'mfa' ? totp : undefined)

      // --- MFA 未設定（管理者強制）→ セットアップページへ ---
      // サーバーは mfaSetupRequired=true と一時セッションCookieを返す。
      // checkAuth で auth/me を叩いて認証状態を確定させたうえで /mfa/setup に遷移する。
      if (result.mfaSetupRequired) {
        setMfaSetupRequired(true)
        toast.info('MFA設定が必要です。セットアップ画面に移動します')
        await checkAuth()
        // ルートがマウントされるまでのタイミング差を避けるため、少し待つ
        setTimeout(() => navigate('/mfa/setup', { replace: true }), 100)
        return
      }

      // --- MFA コード入力が必要 → stage を mfa に遷移 ---
      if (result.mfaRequired) {
        setStage('mfa')
        setTotp('')
        setError('')
        return
      }

      // --- ログイン成功 ---
      if (result.passwordExpired) {
        toast.error('パスワードの有効期限が切れています。変更してください', 6000)
        // 認証済み状態なので設定画面に飛ばす（PasswordChangeDialog が開く想定）
        setTimeout(() => navigate('/settings?passwordChange=1', { replace: true }), 300)
        return
      }
      toast.success('ログインしました')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'ログインに失敗しました'
      setError(msg)
      setLockout(isLockoutMessage(msg))
      // TOTP コードが違っていた場合は入力欄をクリアして再入力を促す
      if (stage === 'mfa') setTotp('')
    } finally {
      setLoading(false)
    }
  }

  const handleBackToCredentials = () => {
    setStage('credentials')
    setTotp('')
    setError('')
  }

  // ZER-334: Passkey ログイン
  //   email 未入力でも動く（discoverable credential：ブラウザがアカウント選択 UI を出す）
  //   入力済みなら allowCredentials でそのユーザーの credential に絞る
  const handlePasskeyLogin = async () => {
    setError('')
    setPasskeyLoading(true)
    try {
      await authenticateWithPasskey(email || undefined)
      await checkAuth()
      toast.success('Passkey でログインしました')
      navigate('/', { replace: true })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Passkey ログインに失敗しました'
      // ユーザーがブラウザの認証ダイアログをキャンセルした場合はエラー表示しない
      if (msg.includes('cancelled') || msg.includes('AbortError') || msg.includes('NotAllowedError')) {
        setError('')
      } else {
        setError(msg)
      }
    } finally {
      setPasskeyLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
            {stage === 'mfa' ? (
              <ShieldCheck className="w-8 h-8 text-blue-600" />
            ) : (
              <Lock className="w-8 h-8 text-blue-600" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {stage === 'mfa' ? '2段階認証' : '資金繰り管理'}
          </h1>
          <p className="text-gray-500 mt-1">
            {stage === 'mfa'
              ? '認証アプリの6桁コードを入力してください'
              : 'ログインしてください'}
          </p>
        </div>

        {mfaSetupRequired && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm mb-4 border border-amber-200">
            <ShieldCheck className="w-4 h-4 flex-shrink-0" />
            MFA設定が必要です。設定画面に移動します...
          </div>
        )}

        {error && (
          <div
            className={`flex items-center gap-2 p-3 rounded-lg text-sm mb-6 ${
              lockout
                ? 'bg-red-100 text-red-800 border border-red-300'
                : 'bg-red-50 text-red-700'
            }`}
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {stage === 'credentials' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">メールアドレス</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                    placeholder="admin@example.com"
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">パスワード</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                    placeholder="パスワード"
                    required
                    autoComplete="current-password"
                  />
                </div>
              </div>
            </>
          )}

          {stage === 'mfa' && (
            <>
              <div className="p-3 rounded-lg bg-blue-50 text-blue-800 text-xs">
                <span className="font-medium">{email}</span> としてログイン中
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">認証コード（6桁）</label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={totp}
                    onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-center tracking-widest text-lg font-mono"
                    placeholder="000000"
                    required
                    autoFocus
                    autoComplete="one-time-code"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Google Authenticator・1Password等の認証アプリに表示されるコードを入力
                </p>
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading || (stage === 'mfa' && totp.length !== 6)}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : stage === 'mfa' ? (
              <>
                <ShieldCheck className="w-4 h-4" />
                認証
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                ログイン
              </>
            )}
          </button>

          {stage === 'mfa' && (
            <button
              type="button"
              onClick={handleBackToCredentials}
              className="w-full text-xs text-gray-500 hover:text-gray-700 underline transition"
            >
              メールアドレス・パスワードの入力に戻る
            </button>
          )}
        </form>

        {/* ZER-334: Passkey ログイン（credentials ステージのみ・対応ブラウザのみ） */}
        {stage === 'credentials' && passkeySupported && (
          <>
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">または</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <button
              type="button"
              onClick={handlePasskeyLogin}
              disabled={passkeyLoading || loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-700 font-medium rounded-lg border border-gray-300 transition"
            >
              {passkeyLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600" />
              ) : (
                <>
                  <Fingerprint className="w-4 h-4 text-indigo-600" />
                  Passkey でログイン
                </>
              )}
            </button>
            <p className="text-xs text-gray-400 text-center mt-2">
              Touch ID / Face ID / Windows Hello 等で瞬時にログイン
            </p>
          </>
        )}
      </div>
    </div>
  )
}
